/**
 * Node ID utilities for creating and parsing node IDs with a unique separator
 */

// Use a unique separator that won't appear in GUIDs or other IDs
export const NODE_ID_SEPARATOR = '<->';

/**
 * Creates a node ID with the specified parts
 */
export function createNodeId(...parts: string[]): string {
  return parts.join(NODE_ID_SEPARATOR);
}

/**
 * Parses a node ID into its component parts
 */
export function parseNodeId(nodeId: string): string[] {
  return nodeId.split(NODE_ID_SEPARATOR);
}

/**
 * Creates a connection node ID
 */
export function createConnectionNodeId(connectionId: string): string {
  return createNodeId('connection', connectionId);
}

/**
 * Creates a databases folder node ID
 */
export function createDatabasesFolderNodeId(connectionId: string): string {
  return createNodeId('databases-folder', connectionId);
}

/**
 * Creates a database node ID
 */
export function createDatabaseNodeId(connectionId: string, databaseName: string): string {
  return createNodeId('database', connectionId, databaseName);
}

/**
 * Creates a tables folder node ID
 */
export function createTablesFolderNodeId(connectionId: string, database: string, schema?: string): string {
  return schema
    ? createNodeId('tables-folder', connectionId, database, schema)
    : createNodeId('tables-folder', connectionId, database);
}

/**
 * Creates a views folder node ID
 */
export function createViewsFolderNodeId(connectionId: string, database: string, schema?: string): string {
  return schema
    ? createNodeId('views-folder', connectionId, database, schema)
    : createNodeId('views-folder', connectionId, database);
}

/**
 * Creates a programmability folder node ID
 */
export function createProgrammabilityFolderNodeId(connectionId: string, database: string, schema?: string): string {
  return schema
    ? createNodeId('programmability-folder', connectionId, database, schema)
    : createNodeId('programmability-folder', connectionId, database);
}

/**
 * Creates a stored procedures folder node ID
 */
export function createStoredProceduresFolderNodeId(connectionId: string, database: string, schema?: string): string {
  return schema
    ? createNodeId('stored-procedures-folder', connectionId, database, schema)
    : createNodeId('stored-procedures-folder', connectionId, database);
}

/**
 * Creates a functions folder node ID
 */
export function createFunctionsFolderNodeId(connectionId: string, database: string, schema?: string): string {
  return schema
    ? createNodeId('functions-folder', connectionId, database, schema)
    : createNodeId('functions-folder', connectionId, database);
}

/**
 * Creates a system databases folder node ID
 */
export function createSystemDatabasesFolderNodeId(connectionId: string): string {
  return createNodeId('system-databases-folder', connectionId);
}

/**
 * Creates a schema-container folder node ID
 */
export function createSchemaContainerFolderNodeId(
  connectionId: string,
  database: string,
  schema: string,
): string {
  return createNodeId('schema-container-folder', connectionId, database, schema);
}

/**
 * Marker prepended to every node id rendered inside a pinned-* subtree. Keeps
 * pinned descendants id-unique vs the canonical drill-down (so `findNodeById`,
 * React keys, and selectedNodeId tracking don't collide), while leaving the
 * underlying type-segments intact (so type-based dispatch in `useNodeExpansion`
 * still works without special-casing).
 *
 * Example: canonical `tables-folder<->cid<->db` becomes
 * `pinned-desc<->{wrapperId}<->tables-folder<->cid<->db` under a pinned-database.
 *
 * The wrapper id (the pinned root the descendant lives under) is part of the
 * prefix so two different pinned wrappers don't collide if a user pins both a
 * database AND a folder inside that database.
 */
export const PINNED_DESCENDANT_PREFIX = 'pinned-desc';

export function pinnedDescendantNodeId(
  pinnedRootId: string,
  canonicalId: string,
): string {
  return createNodeId(PINNED_DESCENDANT_PREFIX, pinnedRootId, canonicalId);
}

/**
 * True iff the given id was produced by `pinnedDescendantNodeId`. Callers use
 * this to detect pinned subtrees in cross-cutting helpers (refresh routing,
 * context menu, ...).
 */
export function isPinnedDescendantNodeId(nodeId: string): boolean {
  return nodeId.startsWith(`${PINNED_DESCENDANT_PREFIX}${NODE_ID_SEPARATOR}`);
}

/**
 * Number of `<->`-joined segments in each pinned-wrapper id shape (see
 * `pinnedNodeId` in nodeFactories.ts). Used to split a `pinned-desc<->...` id
 * back into its `{wrapperRootId}` + `{canonicalLeafId}` parts.
 *
 * Caveat: like every other id parser here, this assumes connection ids /
 * database / schema / object names do NOT themselves contain the `<->`
 * separator. That is the same assumption the rest of `nodeIdUtils` already
 * makes for canonical ids — pin *equality* uses a collision-proof JSON key
 * instead (see `pinRefKey`), but tree-node ids remain separator-joined.
 */
const PINNED_WRAPPER_ARITY: Record<string, number> = {
  'pinned-database': 3, // pinned-database<->cid<->db
  'pinned-schema': 4, // pinned-schema<->cid<->db<->schema
  'pinned-folder': 4, // pinned-folder<->cid<->db<->folder
  'pinned-object': 6, // pinned-object<->cid<->db<->objtype<->schema<->name
  'pinned-storage': 3, // pinned-storage<->cid<->storageResourceKey(ref)
};

/**
 * Given a `pinned-desc<->{wrapperId}<->{canonicalId}` id, return the wrapper
 * root id (the top pinned-* node the descendant lives under), or `null` when
 * the id is not a recognised pinned-descendant id. Works at any nesting depth
 * because every descendant is prefixed with the SAME top wrapper (the prefix is
 * never re-applied), so the second segment is always a pinned-wrapper type.
 */
export function pinnedRootIdFromDescendantId(nodeId: string): string | null {
  if (!isPinnedDescendantNodeId(nodeId)) return null;
  const parts = parseNodeId(nodeId);
  const wrapperType = parts[1];
  const arity = PINNED_WRAPPER_ARITY[wrapperType];
  if (!arity || parts.length < 1 + arity) return null;
  return parts.slice(1, 1 + arity).join(NODE_ID_SEPARATOR);
}

/**
 * Inverse half of {@link pinnedRootIdFromDescendantId}: returns the canonical
 * (un-prefixed) leaf id a pinned descendant wraps, or `null` when the input is
 * not a recognised pinned-descendant id.
 */
export function canonicalLeafIdFromPinnedDescendantId(nodeId: string): string | null {
  if (!isPinnedDescendantNodeId(nodeId)) return null;
  const parts = parseNodeId(nodeId);
  const wrapperType = parts[1];
  const arity = PINNED_WRAPPER_ARITY[wrapperType];
  if (!arity || parts.length < 1 + arity) return null;
  return parts.slice(1 + arity).join(NODE_ID_SEPARATOR);
}

/**
 * Inverse of `pinnedDescendantNodeId` — given a pinned-descendant id, returns
 * the canonical id that was prefixed. Returns the input unchanged if it's not
 * a pinned-descendant id.
 */
export function canonicalFromPinnedDescendantNodeId(nodeId: string): string {
  if (!isPinnedDescendantNodeId(nodeId)) return nodeId;
  const parts = parseNodeId(nodeId);
  // [prefix, wrapperType, wrapperPart1, ...wrapperParts, canonicalType, ...canonicalParts]
  // We can't generically split the wrapper-id from the canonical-id without
  // knowing the wrapper-id segment count. Callers that need the canonical id
  // typically have the wrapper id at hand — they should slice it off instead.
  // This helper handles the simple case: drop the first two segments (prefix
  // + wrapperType) and the next K segments where K = wrapper id length minus
  // its type segment. Without that info, we conservatively return everything
  // after the prefix segment — callers re-slice as needed.
  return parts.slice(1).join(NODE_ID_SEPARATOR);
}

// Oracle-specific folder node IDs
export function createPackagesFolderNodeId(connectionId: string, database: string): string {
  return createNodeId('packages-folder', connectionId, database);
}

export function createSequencesFolderNodeId(connectionId: string, database: string): string {
  return createNodeId('sequences-folder', connectionId, database);
}

export function createSynonymsFolderNodeId(connectionId: string, database: string): string {
  return createNodeId('synonyms-folder', connectionId, database);
}

export function createDbLinksFolderNodeId(connectionId: string, database: string): string {
  return createNodeId('db-links-folder', connectionId, database);
}

export function createMaterializedViewsFolderNodeId(connectionId: string, database: string): string {
  return createNodeId('materialized-views-folder', connectionId, database);
}

export function createTypesFolderNodeId(connectionId: string, database: string): string {
  return createNodeId('types-folder', connectionId, database);
}

export function createDirectoriesFolderNodeId(connectionId: string, database: string): string {
  return createNodeId('directories-folder', connectionId, database);
}

export function createPropertyGraphsFolderNodeId(connectionId: string, database: string): string {
  return createNodeId('property-graphs-folder', connectionId, database);
}

// SQL Server user-defined-type folder node IDs. They carry the optional schema
// segment for the same reason the Programmability folders do: under schema
// grouping each schema container gets its own copy of the subtree.
export function createUserTypesFolderNodeId(connectionId: string, database: string, schema?: string): string {
  return schema
    ? createNodeId('user-types-folder', connectionId, database, schema)
    : createNodeId('user-types-folder', connectionId, database);
}

export function createTableTypesFolderNodeId(connectionId: string, database: string, schema?: string): string {
  return schema
    ? createNodeId('table-types-folder', connectionId, database, schema)
    : createNodeId('table-types-folder', connectionId, database);
}

export function createAliasTypesFolderNodeId(connectionId: string, database: string, schema?: string): string {
  return schema
    ? createNodeId('alias-types-folder', connectionId, database, schema)
    : createNodeId('alias-types-folder', connectionId, database);
}

export function createClrTypesFolderNodeId(connectionId: string, database: string, schema?: string): string {
  return schema
    ? createNodeId('clr-types-folder', connectionId, database, schema)
    : createNodeId('clr-types-folder', connectionId, database);
}

/**
 * Creates an external tables folder node ID (Kusto-specific — Azure Data
 * Explorer external tables, listed via `.show external tables`).
 */
export function createExternalTablesFolderNodeId(connectionId: string, database: string): string {
  return createNodeId('external-tables-folder', connectionId, database);
}

/**
 * Creates a columns folder node ID under a table
 */
export function createColumnsFolderNodeId(
  connectionId: string,
  database: string,
  schema: string,
  tableName: string
): string {
  return createNodeId('columns-folder', connectionId, database, schema, tableName);
}

/**
 * Creates a keys folder node ID under a table
 */
export function createKeysFolderNodeId(
  connectionId: string,
  database: string,
  schema: string,
  tableName: string
): string {
  return createNodeId('keys-folder', connectionId, database, schema, tableName);
}

/**
 * Creates a constraints folder node ID under a table
 */
export function createConstraintsFolderNodeId(
  connectionId: string,
  database: string,
  schema: string,
  tableName: string
): string {
  return createNodeId('constraints-folder', connectionId, database, schema, tableName);
}

/**
 * Creates a triggers folder node ID under a table
 */
export function createTriggersFolderNodeId(
  connectionId: string,
  database: string,
  schema: string,
  tableName: string
): string {
  return createNodeId('triggers-folder', connectionId, database, schema, tableName);
}

/**
 * Creates an indexes folder node ID under a table
 */
export function createIndexesFolderNodeId(
  connectionId: string,
  database: string,
  schema: string,
  tableName: string
): string {
  return createNodeId('indexes-folder', connectionId, database, schema, tableName);
}

/**
 * Creates a statistics folder node ID under a table
 */
export function createStatisticsFolderNodeId(
  connectionId: string,
  database: string,
  schema: string,
  tableName: string
): string {
  return createNodeId('statistics-folder', connectionId, database, schema, tableName);
}

/**
 * Creates a partitions folder node ID under a (partitioned) table
 */
export function createPartitionsFolderNodeId(
  connectionId: string,
  database: string,
  schema: string,
  tableName: string
): string {
  return createNodeId('partitions-folder', connectionId, database, schema, tableName);
}

/**
 * Creates a key node ID (PK/FK/UNIQUE)
 */
export function createKeyNodeId(
  connectionId: string,
  database: string,
  schema: string,
  tableName: string,
  keyName: string
): string {
  return createNodeId('key', connectionId, database, schema, tableName, keyName);
}

/**
 * Creates a constraint node ID (CHECK/DEFAULT)
 */
export function createConstraintNodeId(
  connectionId: string,
  database: string,
  schema: string,
  tableName: string,
  constraintName: string
): string {
  return createNodeId('constraint', connectionId, database, schema, tableName, constraintName);
}

/**
 * Creates an index node ID
 */
export function createIndexNodeId(
  connectionId: string,
  database: string,
  schema: string,
  tableName: string,
  indexName: string
): string {
  return createNodeId('index', connectionId, database, schema, tableName, indexName);
}

/**
 * Creates a trigger node ID
 */
export function createTriggerNodeId(
  connectionId: string,
  database: string,
  schema: string,
  tableName: string,
  triggerName: string
): string {
  return createNodeId('trigger', connectionId, database, schema, tableName, triggerName);
}

/**
 * Creates a statistic node ID
 */
export function createStatisticNodeId(
  connectionId: string,
  database: string,
  schema: string,
  tableName: string,
  statisticName: string
): string {
  return createNodeId('statistic', connectionId, database, schema, tableName, statisticName);
}

/**
 * Creates a partition node ID. `tableName` is the partitioned PARENT so the id
 * is unique per parent; `partitionName` is the child partition's own relname.
 */
export function createPartitionNodeId(
  connectionId: string,
  database: string,
  schema: string,
  tableName: string,
  partitionName: string
): string {
  return createNodeId('partition', connectionId, database, schema, tableName, partitionName);
}

/**
 * Creates a column node ID
 */
export function createColumnNodeId(
  connectionId: string,
  database: string,
  schema: string,
  tableName: string,
  columnName: string
): string {
  return createNodeId('column', connectionId, database, schema, tableName, columnName);
}

/**
 * Creates a database object node ID
 * @param connectionId The connection ID
 * @param schemaOrDatabase Either the schema (if using 3 params) or the database name (if using 5 params)
 * @param objectNameOrType Either the object name (if using 3 params) or the object type (if using 5 params)
 * @param schema The schema (only when using 5 params)
 * @param objectName The object name (only when using 5 params)
 */
export function createDatabaseObjectNodeId(
  connectionId: string,
  schemaOrDatabase: string | undefined,
  objectNameOrType: string,
  schema?: string | undefined,
  objectName?: string
): string {
  // If we have 5 parameters (full signature), create the detailed ID
  if (objectName !== undefined) {
    // This is the 5-parameter version (connectionId, database, objectType, schema, objectName)
    return schema
      ? createNodeId('object', connectionId, schemaOrDatabase || '', objectNameOrType, schema, objectName)
      : createNodeId('object', connectionId, schemaOrDatabase || '', objectNameOrType, objectName);
  }
  
  // Otherwise, use the original 3-parameter signature (connectionId, schema, objectName)
  return schemaOrDatabase
    ? createNodeId('object', connectionId, schemaOrDatabase, objectNameOrType)
    : createNodeId('object', connectionId, objectNameOrType);
}

/**
 * Extracts the database name from a node ID
 * Returns undefined if the node ID doesn't contain a database name
 */
export function getDatabaseFromNodeId(nodeId: string): string | undefined {
  const parts = parseNodeId(nodeId);

  // Check if this is a database node
  if (parts[0] === 'database' && parts.length >= 3) {
    return parts[2]; // Database name is the third part
  }

  // Check if this is a folder under a database
  if (['schema-container-folder', 'tables-folder', 'views-folder', 'programmability-folder',
       'stored-procedures-folder', 'functions-folder',
       'packages-folder', 'sequences-folder', 'synonyms-folder',
       'db-links-folder', 'materialized-views-folder', 'types-folder',
       'directories-folder', 'external-tables-folder', 'property-graphs-folder',
       'user-types-folder', 'table-types-folder', 'alias-types-folder', 'clr-types-folder'].includes(parts[0]) &&
      parts.length >= 3) {
    return parts[2]; // Database name is the third part
  }

  // Check if this is a table subfolder (columns, keys, etc.)
  // Format: folder-type<->connectionId<->database<->schema<->tableName
  if (['columns-folder', 'keys-folder', 'constraints-folder',
       'triggers-folder', 'indexes-folder', 'statistics-folder',
       'partitions-folder'].includes(parts[0]) &&
      parts.length >= 3) {
    return parts[2]; // Database name is the third part
  }

  // Check if this is a column node
  // Format: column<->connectionId<->database<->schema<->tableName<->columnName
  if (parts[0] === 'column' && parts.length >= 3) {
    return parts[2]; // Database name is the third part
  }

  // Pinned wrapper nodes — same `cid<->database<->...` shape as the underlying
  // node types so the selection-context helpers (uiStore.setSelectedNodeId,
  // MCP context sync) can read the database from a pinned-row click without
  // any extra dispatch. Without this branch, clicking a pinned row would wipe
  // the selectedDatabase + MCP selection context (finding F6).
  if (
    (parts[0] === 'pinned-database' ||
      parts[0] === 'pinned-schema' ||
      parts[0] === 'pinned-folder' ||
      parts[0] === 'pinned-object') &&
    parts.length >= 3
  ) {
    return parts[2];
  }

  // For database objects, the database name might be in the metadata
  // This function can't access that, so we'll need to handle that case elsewhere

  return undefined;
}

/**
 * Marker segment of a workspace-pin tree-node id
 * (`${connectionNodeId}:pinned-workspace:${pinId}` — see
 * `createPinnedWorkspaceNode`). Kept in sync with the copy in
 * `mcp-context/services/selectionSnapshot.ts`.
 */
const WORKSPACE_PIN_ID_MARKER = ':pinned-workspace:';

/**
 * Extracts the connection ID from a node ID
 * Returns undefined if the node ID doesn't contain a connection ID
 *
 * Workspace-pin rows are the one node type built outside the `<->` scheme:
 * `createPinnedWorkspaceNode` appends `:pinned-workspace:{pinId}` to the parent
 * connection node id, so `parseNodeId` yields
 * `['connection', '{cid}:pinned-workspace:{pinId}']` and `parts[1]` is the
 * connection id with the pin tail glued on — what `nodeFactories` calls a
 * "mangled connection id". uiStore stores that as `selectedConnectionId`, and
 * every consumer matching it against a real connection with `===` silently
 * misses: `contextSelectionStore` failed to resolve the selected connection and
 * popped the connection picker on New Query / Table Explorer / Import Data
 * whenever 2+ connections were connected (with exactly one it got rescued by
 * the auto-select fallback, which is why this hid for so long).
 *
 * Strip the tail here so the id is clean for every consumer, rather than making
 * each one special-case the marker the way `selectionSnapshot` had to.
 */
export function getConnectionIdFromNodeId(nodeId: string): string | undefined {
  const parts = parseNodeId(nodeId);

  // Most node types have connection ID as the second part
  if (parts.length >= 2) {
    const markerIndex = parts[1].indexOf(WORKSPACE_PIN_ID_MARKER);
    return markerIndex >= 0 ? parts[1].slice(0, markerIndex) : parts[1];
  }

  return undefined;
}

/**
 * Checks if a node ID belongs to a specific connection
 * This includes the connection node itself and all its child nodes
 */
export function isNodeFromConnection(nodeId: string | undefined, connectionId: string): boolean {
  if (!nodeId) return false;

  const parts = parseNodeId(nodeId);

  // Check if this is the connection node itself
  if (parts[0] === 'connection' && parts[1] === connectionId) {
    return true;
  }

  // Check if this is a child node of the connection
  // For child nodes, the connection ID is typically the second part
  if (parts.length >= 2 && parts[1] === connectionId) {
    return true;
  }

  return false;
}

 